import { parsePhoneNumber } from 'react-phone-number-input'
import { useParams } from 'react-router'

import { Button } from 'common/components/Button/Button'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import { formatDate } from 'common/services/format'
import type { Patient } from 'features/Procedure/state/procedure-api.type'
import {
  useFetchEncounterPatientsQuery,
  useFetchPhysicianIntroductionsQuery,
} from 'features/Procedure/state/procedure.api'

import styles from './patient-info-dialog.module.scss'
import {
  findDiscountCategoryTitle,
  findDiscountTypeTitle,
  getApplicableDiscountFields,
} from './patient-discount'
import { buildDefaultPhysicianLabel, findDefaultPhysician } from './physician-introductions.utils'

type Props = {
  patientInfo: Patient
  onEdit: () => void
}

const formatPhone = (phone?: string | null): string => {
  if (!phone) return ''
  try {
    return parsePhoneNumber(phone)?.formatInternational() ?? phone
  } catch {
    return phone
  }
}

export const PatientInfoView = ({ patientInfo, onEdit }: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const { data: encounterPatients } = useFetchEncounterPatientsQuery(
    { siteId: siteId! },
    { skip: !siteId },
  )

  const { currentData: physicians } = useFetchPhysicianIntroductionsQuery(
    { siteId: siteId!, patientId: patientInfo.patient_id },
    { skip: !siteId || !patientInfo.patient_id },
  )

  const defaultPhysician = findDefaultPhysician(physicians ?? [])
  const defaultPhysicianName = defaultPhysician ? buildDefaultPhysicianLabel(defaultPhysician) : ''

  const applicableDiscount = getApplicableDiscountFields(patientInfo.discount_category)
  const discountCategoryTitle = findDiscountCategoryTitle(
    encounterPatients?.discount_categories,
    patientInfo.discount_category,
  )
  const discountProductsTitle = findDiscountTypeTitle(
    encounterPatients?.discount_type,
    patientInfo.discount_type_products,
  )
  const discountProceduresTitle = findDiscountTypeTitle(
    encounterPatients?.discount_type,
    patientInfo.discount_type_procedures,
  )

  const patientName = `${patientInfo.last_name || 'N/A'}, ${patientInfo.first_name ?? ''}`
  const fullAddress = [
    patientInfo.address,
    patientInfo.city,
    patientInfo.state,
    patientInfo.zip_code,
    patientInfo.country,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerMain}>
          <h1 className={styles.patientName}>{patientName}</h1>
          <Button
            modifier={BUTTON_MODIFIER.PRIMARY}
            proportion={BUTTON_PROPORTION.SMALL}
            onClick={onEdit}>
            Edit
          </Button>
        </div>
        {defaultPhysicianName && (
          <span className={styles.defaultPhysician}>{defaultPhysicianName}</span>
        )}
      </div>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Contact</h3>
        <div className={styles.contactInfo}>
          {(patientInfo.dob || patientInfo.age) && (
            <div className={styles.contactRow}>
              {patientInfo.dob && (
                <>
                  <span className={styles.label}>DOB:</span>
                  <span className={styles.value}>{formatDate(patientInfo.dob)}</span>
                </>
              )}
              {patientInfo.age && (
                <>
                  <span className={styles.label}>Age:</span>
                  <span className={styles.value}>{patientInfo.age}</span>
                </>
              )}
            </div>
          )}
          {(patientInfo.phone || patientInfo.email) && (
            <div className={styles.contactRow}>
              {patientInfo.phone && (
                <>
                  <span className={styles.label}>Phone:</span>
                  <span className={styles.value}>{formatPhone(patientInfo.phone)}</span>
                </>
              )}
              {patientInfo.email && (
                <>
                  <span className={styles.label}>Email:</span>
                  <span className={styles.value}>{patientInfo.email}</span>
                </>
              )}
            </div>
          )}
          {fullAddress && (
            <div className={styles.contactRow}>
              <span className={styles.label}>Address:</span>
              <span className={styles.value}>{fullAddress}</span>
            </div>
          )}
        </div>
      </section>

      {patientInfo.referral_source && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Info</h3>
          <div className={styles.contactInfo}>
            <div className={styles.contactRow}>
              <span className={styles.label}>Referred by:</span>
              <span className={styles.value}>{patientInfo.referral_source}</span>
            </div>
          </div>
        </section>
      )}

      {patientInfo.discount_category && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Discount</h3>
          <div className={styles.contactInfo}>
            <div className={styles.contactRow}>
              <span className={styles.label}>Category:</span>
              <span className={styles.value}>
                {discountCategoryTitle ?? patientInfo.discount_category}
              </span>
            </div>
            {applicableDiscount.products && discountProductsTitle && (
              <div className={styles.contactRow}>
                <span className={styles.label}>Products:</span>
                <span className={styles.value}>{discountProductsTitle}</span>
              </div>
            )}
            {applicableDiscount.procedures && discountProceduresTitle && (
              <div className={styles.contactRow}>
                <span className={styles.label}>Procedures:</span>
                <span className={styles.value}>{discountProceduresTitle}</span>
              </div>
            )}
          </div>
        </section>
      )}

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Billing Notes</h3>
        <div className={styles.noteText}>{patientInfo.billing_note || 'N/A'}</div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>General Note</h3>
        <div className={styles.noteText}>{patientInfo.general_note || 'N/A'}</div>
      </section>
    </div>
  )
}
